import { pipe, catchError } from 'overmind';

import type { CalculationState } from '@/features/execute-calculation-set/overmind/calculation';
import type { Context } from '@/overmind';
import { getRequestAborter } from '@/overmind/effects/requestAborter';
import { UnexpectedStateError, AbortError } from '@/overmind/errors';
import { routes } from '@/overmind/machines';

import type { MapConfiguration } from '../config/config.types';

const { switchToNewRequest, completeRequestOperator } =
  getRequestAborter('getArcGISToken');

/*
TODO:
Scenarios to cover:
"old static named user token provisioning" test cases:
* fetching token when tenant is set
* fetching token when tenant is not set
* should skip fetching token when token is already in progress
*/

export const getArcGISToken = pipe(
  async ({ state, effects }: Context) => {
    const abortController = switchToNewRequest();
    if (
      (state.auth.current === 'AWAITING_ARCGIS_AUTHENTICATION' ||
        state.auth.current === 'AUTHENTICATING_ARCGIS') &&
      state.tenants.currentTenant.type === 'SELECTED_TENANT'
    ) {
      state.auth.send('ARCGIS_AUTHENTICATING');
      const externalArcGisToken = getExternalArcGisToken(state.calculation);
      if (externalArcGisToken != null) {
        return externalArcGisToken;
      }
      const tenant = state.tenants.currentTenant.context.tenantId;
      return effects.api.networkService.getArcGisToken(tenant, abortController);
    }

    throw new UnexpectedStateError();
  },
  ({ state }: Context, arcGisToken) => {
    state.auth.send('ARCGIS_AUTHENTICATION_COMPLETED', arcGisToken);
  },
  catchError((_: Context, error) => {
    if (
      !(error instanceof UnexpectedStateError || error instanceof AbortError)
    ) {
      // ts-ignore
    }
  }),
  completeRequestOperator()
);

export const handleAlreadySignedInArcGisUser = async (
  ctx: Context,
  config: MapConfiguration
) => {
  if (
    ctx.state.calculation.externallyTriggeredCalculationSetExecution.current !==
      'IDLE' &&
    !ctx.state.calculation.externallyTriggeredCalculationSetExecution
      .calculationContext.arcgisToken
  ) {
    const tokenResponse = await ctx.effects.arcgis.auth.identity.getAccessToken(
      { withMaxAttempts: 5 }
    );
    if (!tokenResponse.arcgisToken) {
      ctx.state.auth.send('NO_EXTERNALLY_AVAILABLE_ARCGIS_TOKEN');
      ctx.effects.storage.set(
        'calculationContext',
        ctx.state.calculation.externallyTriggeredCalculationSetExecution
          .calculationContext
      );
      return;
    } else {
      // ctx.state.auth.send("TOKEN_AVAILABLE_EXTERNALLY", { arcgisToken: tokenResponse.arcgisToken });
      ctx.state.auth.send(
        'ARCGIS_AUTHENTICATION_COMPLETED',
        tokenResponse.arcgisToken
      );
      ctx.effects.router.goTo({
        url: routes.executeCalculationSetUsing,
        query: {
          ...ctx.state.calculation.externallyTriggeredCalculationSetExecution
            .calculationContext,
          arcgisToken: tokenResponse.arcgisToken
        }
      });
      ctx.effects.storage.set('calculationContext', undefined);
      return;
    }
  }
  const isAuthenticated =
    await ctx.effects.arcgis.auth.identity.isAuthenticated(config);

  if (isAuthenticated) {
    const arcGisToken = (await ctx.effects.auth.getToken()) as string;
    ctx.state.auth.send('ARCGIS_AUTHENTICATION_COMPLETED', arcGisToken);
  }
  ctx.actions.arcgis.authenticateManually();
};

function getExternalArcGisToken(state: CalculationState) {
  return state.externallyTriggeredCalculationSetExecution.matches('READY')
    ?.calculationContext.arcgisToken;
}
